import React from "react";

import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import toPairs from "lodash/toPairs";
import moment, { Moment } from "moment-timezone";
import { Link } from "react-router-dom";

import PanelSection from "components/PanelSection";

import QUERY from "./Query.graphql";

import directory from "../../../../docs/directory.json";
import { useQuery } from "@apollo/client";
import Loading from "components/Loading";

import {
  LogStats_getLogStats_stats,
  LogStats as LogStatsType,
  LogStatsVariables,
  LogStats_getLogQuotaUsage,
} from "./types/LogStats";
import { useDateRange } from "components/WithDateRange";
import TimeBucketBar from "components/TimeBucketBar";
import ShowFlash from "components/ShowFlash";

import Color from "color";
import { useRoutes } from "utils/routes";
import { formatBytes } from "utils/format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearchPlus } from "@fortawesome/pro-regular-svg-icons";
import Button from "components/Button";
import { ExpandToggle } from "components/Icons";
import { useUserPreferences } from "utils/hooks";

type LogCategory = keyof typeof directory.logCategories;
type LogStatsValue = Omit<LogStats_getLogStats_stats, "__typename">;

type LogClassificationStatsType = [string, LogStatsValue[]];
type LogClassificationStatsCategoryType = [
  string,
  LogClassificationStatsType[],
];

type Props = {
  serverId: string;
  databaseId?: string;
  selectedClassification: string | null | undefined;
  highlightedTime: moment.Moment | null | undefined;
};

const LogStats: React.FunctionComponent<Props> = ({
  databaseId,
  serverId,
  ...rest
}) => {
  const { from, to } = useDateRange();
  const startTs = from.unix();
  const endTs = to.unix();
  const { data, loading, error } = useQuery<LogStatsType, LogStatsVariables>(
    QUERY,
    {
      variables: {
        databaseId,
        serverId,
        startTs,
        endTs,
      },
    },
  );
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <>
      <LogQuotaWarning quotaInfo={data.getLogQuotaUsage} />
      <LogStatsDisplay
        databaseId={databaseId}
        serverId={serverId}
        data={data}
        startTs={startTs}
        endTs={endTs}
        {...rest}
      />
    </>
  );
};

type DisplayProps = Props & {
  data: LogStatsType;
  startTs: number;
  endTs: number;
};

const LogStatsDisplay: React.FunctionComponent<DisplayProps> = ({
  databaseId,
  serverId,
  data,
  startTs,
  endTs,
  selectedClassification,
  highlightedTime,
}) => {
  const [expandedState, setExpandedState] = useUserPreferences("logInsights");

  const logStats = data.getLogStats.stats;
  if (!logStats || logStats.length === 0) {
    return (
      <PanelSection>
        <strong>Error:</strong> No logs were received
        {databaseId === null ? "" : " for this database"} in the selected
        timeframe.
      </PanelSection>
    );
  }

  if (selectedClassification) {
    const stats = logStats.filter(
      (l: LogStatsValue): boolean => l.classification == selectedClassification,
    );

    return (
      <ClassificationDetails
        logStats={stats}
        serverId={serverId}
        databaseId={databaseId}
        classification={selectedClassification}
        highlightedTime={highlightedTime}
        endTs={endTs}
        startTs={startTs}
      />
    );
  }

  const classificationDetailsKeys = Object.keys(directory.logClassifications);
  const categoriesKeys = Object.keys(directory.logCategories);
  let logStatsByClassification: LogClassificationStatsType[] = toPairs(
    groupBy(logStats, (l: LogStatsValue): string => l.classification),
  );

  logStatsByClassification = sortBy(
    logStatsByClassification,
    ([classification]): number => {
      const idx = classificationDetailsKeys.indexOf(classification);
      return idx === -1 ? Number.MAX_VALUE : idx;
    },
  );

  let classificationStatsByCategory: LogClassificationStatsCategoryType[] =
    toPairs(
      groupBy(
        logStatsByClassification,
        (l) =>
          directory.logClassifications[
            l[0] as keyof typeof directory.logClassifications
          ].category,
      ),
    );

  classificationStatsByCategory = sortBy(
    classificationStatsByCategory,
    ([category]): number => {
      const idx = categoriesKeys.indexOf(category);
      return idx === -1 ? Number.MAX_VALUE : idx;
    },
  );

  const expandedCategories =
    expandedState.expandedCategories ??
    (["app-errors", "locks", "constraint-violations"] satisfies LogCategory[]);

  return (
    <div className="py-[5px] px-[8px] border-b border-[#eee] grid grid-cols-[180px_1fr]">
      {classificationStatsByCategory.map(([category, categoryStats]) => {
        const isExpanded = expandedCategories.some((cat) => cat === category);
        function handleExpandedToggled() {
          const existingExpanded = expandedCategories;
          const isExpanded = existingExpanded.some((cat) => cat == category);
          const newExpanded = isExpanded
            ? existingExpanded.filter((cat) => cat != category)
            : existingExpanded.concat(category);

          setExpandedState({
            ...expandedState,
            expandedCategories: newExpanded,
          });
        }
        return (
          <CategoryLogStats
            key={category}
            category={category as LogCategory}
            highlightedTime={highlightedTime}
            endTs={endTs}
            startTs={startTs}
            serverId={serverId}
            databaseId={databaseId}
            categoryStats={categoryStats}
            isExpanded={isExpanded}
            onExpandedToggled={handleExpandedToggled}
          />
        );
      })}
    </div>
  );
};

const CategoryLogStats = ({
  category,
  databaseId,
  serverId,
  highlightedTime,
  endTs,
  startTs,
  categoryStats,
  isExpanded,
  onExpandedToggled,
}: {
  category: LogCategory;
  databaseId: string;
  serverId: string;
  highlightedTime: moment.Moment;
  endTs: number;
  startTs: number;
  categoryStats: LogClassificationStatsType[];
  isExpanded: boolean;
  onExpandedToggled: () => void;
}) => {
  const categoryTitle = directory.logCategories[category];
  return (
    <>
      <Button
        className="flex items-center gap-1.5 mt-4 first-of-type:mt-0"
        bare
        onClick={onExpandedToggled}
      >
        <ExpandToggle className="w-3" expanded={isExpanded} />
        <div className="font-bold whitespace-nowrap">{categoryTitle}</div>
      </Button>
      {isExpanded ? (
        <>
          <div />
          {categoryStats.map(([classification, stats]) => (
            <ClassificationLogStats
              key={classification}
              category={category}
              stats={stats}
              classification={classification}
              databaseId={databaseId}
              serverId={serverId}
              highlightedTime={highlightedTime}
              endTs={endTs}
              startTs={startTs}
            />
          ))}
        </>
      ) : (
        <CategoryLogStatsSummary
          className="mt-4 first-of-type:mt-0"
          category={category}
          categoryStats={categoryStats}
          databaseId={databaseId}
          serverId={serverId}
          highlightedTime={highlightedTime}
          endTs={endTs}
          startTs={startTs}
        />
      )}
    </>
  );
};

const CategoryLogStatsSummary = ({
  className,
  databaseId,
  serverId,
  highlightedTime,
  endTs,
  startTs,
  category,
  categoryStats,
}: {
  className?: string;
  databaseId: string;
  serverId: string;
  highlightedTime: moment.Moment;
  endTs: number;
  startTs: number;
  category: LogCategory;
  categoryStats: LogClassificationStatsType[];
}) => {
  const categoryTitle = directory.logCategories[category];
  const timebucketIndexes = new Map<number, number>();
  const summaryStats = categoryStats
    .flatMap(([_classification, stats]) => stats)
    .reduce<LogStatsValue[]>((accum, stats) => {
      const { timebucket } = stats;
      if (timebucketIndexes.has(timebucket)) {
        const bucketIdx = timebucketIndexes.get(timebucket);
        accum[bucketIdx].count += stats.count;
      } else {
        const newBucketIdx = accum.length;
        timebucketIndexes.set(timebucket, newBucketIdx);
        accum.push({
          timebucket: timebucket,
          classification: `${categoryTitle} Events`,
          count: stats.count,
        });
      }
      return accum;
    }, []);
  const maxCount = summaryStats.reduce<number>(
    (max, curr) => Math.max(max, curr.count),
    0,
  );
  return (
    <Stats
      className={className}
      databaseId={databaseId}
      serverId={serverId}
      highlightedTime={highlightedTime}
      endTs={endTs}
      startTs={startTs}
      category={category}
      stats={summaryStats}
      maxCount={maxCount}
    />
  );
};

type ClassificationLogStatsProps = {
  category: LogCategory;
  stats: LogStatsValue[];
  classification: string;
  databaseId: string;
  serverId: string;
  endTs: number;
  startTs: number;
  highlightedTime: Moment;
};

// From 10-color set in https://mk.bcgsc.ca/colorblind/distinct.colors.mhtml
const CATEGORY_COLORS: Record<LogCategory, string> = {
  server: "#9c355f",
  connections: "#446728",
  "wal-checkpoints": "#76b056",
  autovacuum: "#00a5a5",
  locks: "#cc76c0",
  statements: "#da7457",
  standby: "#668fd3",
  "constraint-violations": "#5f4799",
  "app-errors": "#8a4719",
  unknown: "#c39643",
};

function getCategoryColor(category: LogCategory): Color {
  const color = CATEGORY_COLORS[category];
  return new Color(color);
}

const ClassificationLogStats: React.FunctionComponent<
  ClassificationLogStatsProps
> = ({
  category,
  stats,
  classification,
  databaseId,
  serverId,
  endTs,
  startTs,
  highlightedTime,
}) => {
  const { databaseLogs, serverLogs } = useRoutes();
  const maxCount = Math.max(
    ...stats.map((g: LogStatsValue): number => g.count),
  );
  const details =
    directory.logClassifications[
      classification as keyof typeof directory.logClassifications
    ];

  let classificationPath = "";
  if (databaseId) {
    classificationPath = databaseLogs(databaseId, classification);
  } else if (serverId) {
    classificationPath = serverLogs(serverId, classification);
  }

  return (
    <>
      <div className="pr-[5px] h-5 relative cursor-pointer">
        <Link
          to={classificationPath}
          className="text-inherit hover:text-inherit focus:text-inherit"
        >
          <span
            title="Click for more details"
            className="group overflow-hidden inline-block text-ellipsis whitespace-nowrap absolute top-0 left-0 max-w-[180px] hover:top-[-6px] hover:left-[-11px] hover:max-w-none hover:bg-[rgba(255,255,255,0.9)] hover:px-[10px] hover:py-[5px] hover:border hover:border-[#ddd] hover:shadow-sm hover:z-[2] hover:rounded-sm"
          >
            <FontAwesomeIcon
              icon={faSearchPlus}
              className="text-[#777] hover:text-[#333]"
            />{" "}
            {details.title}
            <span className="hidden group-hover:inline">
              {` (${classification})`}
            </span>
          </span>
        </Link>
      </div>
      <div className="border-[#eee] h-5">
        <Stats
          serverId={serverId}
          databaseId={databaseId}
          category={category}
          stats={stats}
          maxCount={maxCount}
          highlightedTime={highlightedTime}
          endTs={endTs}
          startTs={startTs}
          link
        />
      </div>
    </>
  );
};

type StatsProps = {
  className?: string;
  stats: LogStatsValue[];
  category: LogCategory;
  maxCount: number;
  highlightedTime: Moment;
  endTs: number;
  startTs: number;
  serverId: string;
  databaseId: string;
  link?: boolean;
};

const Stats: React.FunctionComponent<StatsProps> = ({
  className,
  category,
  stats,
  maxCount,
  highlightedTime,
  endTs,
  startTs,
  serverId,
  databaseId,
  link = false,
}) => {
  const { databaseLogs, serverLogs } = useRoutes();
  if (stats.length === 0) {
    return null;
  }
  const from = moment.unix(startTs);
  const to = moment.unix(endTs);
  const bucketSize = moment.duration(
    to.diff(from, "hours", true) <= 3
      ? 1
      : to.diff(from, "days", true) <= 3
      ? 10
      : 60,
    "minutes",
  );

  const linkable = link && (databaseId != null || serverId != null);
  const buckets = stats
    .filter((group) => group.timebucket >= startTs)
    .map((group) => {
      const start = moment.unix(group.timebucket);
      const end = start.clone().add(bucketSize);
      const url =
        linkable &&
        (databaseId
          ? databaseLogs(databaseId, group.classification)
          : serverLogs(serverId, group.classification)) + `#t=${end.unix()}`;
      return {
        start,
        value: group.count,
        url,
      };
    });
  const representativeGroup = stats[0];
  const classification =
    representativeGroup.classification as keyof typeof directory.logClassifications;
  const details = directory.logClassifications[classification];

  const color = getCategoryColor(category);

  const TimeBucketTip: React.FunctionComponent<{
    start: Moment;
    end: Moment;
    value: number;
  }> = ({ start, end, value }) => {
    return (
      <div className="min-w-64">
        <strong>{value}</strong> &times;{" "}
        {details
          ? `${representativeGroup.classification}: ${details.title}`
          : representativeGroup.classification || "?"}
        <div className="text-[#777]">
          {start.format("MMM DD hh:mma")}
          &mdash;
          {end.format("hh:mma zz")}
        </div>
      </div>
    );
  };
  return (
    <TimeBucketBar
      className={className}
      from={from}
      to={to}
      highlight={highlightedTime}
      buckets={buckets}
      bucketSize={bucketSize}
      maxValue={maxCount}
      color={Color(color)}
      tip={TimeBucketTip}
    />
  );
};

type ClassificationDetailsProps = {
  logStats: LogStatsValue[];
  classification: string;
  highlightedTime: Moment;
  endTs: number;
  startTs: number;
  serverId: string;
  databaseId: string;
};

const ClassificationDetails: React.FunctionComponent<
  ClassificationDetailsProps
> = ({
  logStats,
  classification,
  highlightedTime,
  endTs,
  startTs,
  serverId,
  databaseId,
}) => {
  const category = directory.logClassifications[
    classification as keyof typeof directory.logClassifications
  ].category as LogCategory;
  const maxCount = Math.max(
    ...logStats.map((g: LogStatsValue): number => g.count),
  );

  return (
    <PanelSection>
      <table className="w-full">
        <tbody>
          <tr>
            <td className="border border-[#eee] h-5">
              <Stats
                serverId={serverId}
                databaseId={databaseId}
                category={category}
                stats={logStats}
                maxCount={maxCount}
                highlightedTime={highlightedTime}
                endTs={endTs}
                startTs={startTs}
                link={false}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </PanelSection>
  );
};

const LogQuotaWarning: React.FunctionComponent<{
  quotaInfo: LogStats_getLogQuotaUsage;
}> = ({ quotaInfo }) => {
  const { bytesOverQuota } = quotaInfo;
  if (bytesOverQuota === 0) {
    return null;
  }
  return (
    <ShowFlash
      level="alert"
      msg={
        <>
          This server exceeded its log rate limit by{" "}
          {formatBytes(bytesOverQuota)} in the selected time period; some log
          lines were dropped.{" "}
          <a
            target="_blank"
            rel="noopener"
            href="https://pganalyze.com/docs/log-insights/limits"
          >
            Learn more
          </a>
          .
        </>
      }
    />
  );
};

export default React.memo(LogStats);
